/* generated using openapi-typescript-codegen -- do not edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { FormDefinition } from '../models/FormDefinition';
import type { FormDefinitionChangeDto } from '../models/FormDefinitionChangeDto';
import type { FormDefinitionDraftResult } from '../models/FormDefinitionDraftResult';
import type { FormElementBase } from '../models/FormElementBase';
import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';
export class FormDefinitionService {
    constructor(public readonly httpRequest: BaseHttpRequest) {}
    /**
     * Retrieves all form definitions from the database, with optional filtering by name.
     * This endpoint returns a list of all form definitions, excluding those of type ComplianceItemDefinition.
     * The results can be filtered by providing a search query that matches against form names.
     * The returned form definitions do not include their components or rules to minimize payload size.
     * @param query Optional search query to filter form names by.
     * If provided, only forms with names containing this string (case-insensitive) will be returned.
     * @returns FormDefinition OK
     * @throws ApiError
     */
    public getAllFormDefinitions(
        query?: string,
    ): CancelablePromise<Array<FormDefinition>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/FormDefinition/GetAllFormDefinitions',
            query: {
                'query': query,
            },
            errors: {
                401: `Unauthorized`,
            },
        });
    }
    /**
     * Retrieves a single form definition by its unique identifier (GUID).
     * This endpoint returns a complete form definition including all its components (ordered by FormOrder),
     * component events, element rules, section rules, and form rules. It also includes the count of
     * form responses that have been submitted for this form definition.
     *
     * The query uses AsSplitQuery() to optimize the loading of multiple related collections.
     * @param formDefinitionGuid The unique identifier (GUID) of the form definition to retrieve
     * @returns FormDefinition OK
     * @throws ApiError
     */
    public getFormDefinition(
        formDefinitionGuid: string,
    ): CancelablePromise<FormDefinition> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/FormDefinition/{formDefinitionGuid}/GetFormDefinition',
            path: {
                'formDefinitionGuid': formDefinitionGuid,
            },
            errors: {
                401: `Unauthorized`,
            },
        });
    }
    /**
     * Updates an existing form definition with new content, including its components, rules, and events.
     * This is a complex operation that handles:
     * - Updating the form definition properties
     * - Managing form elements (components) including their order
     * - Handling element events
     * - Processing form rules, section rules, and element rules
     * - Deleting elements that are no longer present in the updated form
     *
     * The method uses a database transaction to ensure all changes are applied atomically.
     * If any form responses exist for this form, elements will be archived rather than deleted.
     *
     * After successful update, a real-time notification is sent to all clients viewing the form
     * via SignalR to inform them of the changes.
     * @param formDefinitionGuid The unique identifier (GUID) of the form definition to update
     * @param requestBody The complete updated form definition with all its components and rules
     * @returns FormDefinition OK
     * @throws ApiError
     */
    public updateFormDefinition(
        formDefinitionGuid: string,
        requestBody?: FormDefinition,
    ): CancelablePromise<FormDefinition> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/api/FormDefinition/{formDefinitionGuid}/UpdateFormDefinition',
            path: {
                'formDefinitionGuid': formDefinitionGuid,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                401: `Unauthorized`,
            },
        });
    }
    /**
     * Creates a draft for the form (a per-element-row snapshot of its current definition)
     * or resumes the existing one. All designer edits to an in-use form go to the draft;
     * the live definition is only changed when the draft is committed.
     * @param formDefinitionGuid The form to draft.
     * @returns FormDefinitionDraftResult OK
     * @throws ApiError
     */
    public createOrResumeDraft(
        formDefinitionGuid: string,
    ): CancelablePromise<FormDefinitionDraftResult> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/FormDefinition/{formDefinitionGuid}/CreateOrResumeDraft',
            path: {
                'formDefinitionGuid': formDefinitionGuid,
            },
            errors: {
                401: `Unauthorized`,
            },
        });
    }
    /**
     * Saves one or more draft elements in a single call (issue #1015 — a scoped save that
     * touches only the changed element rows, not the whole form). Upserts each element row,
     * touches the draft header's timestamp, and records ONE draft-edit history entry covering
     * all of them. The live definition is untouched. Accepts a list so a designer save that
     * changed several elements is one request and one log entry.
     * @param formDefinitionGuid The form whose draft is being edited.
     * @param requestBody The full edited elements (one or many).
     * @returns FormDefinitionDraftResult OK
     * @throws ApiError
     */
    public updateDraftElements(
        formDefinitionGuid: string,
        requestBody?: Array<FormElementBase>,
    ): CancelablePromise<FormDefinitionDraftResult> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/api/FormDefinition/{formDefinitionGuid}/draft/elements',
            path: {
                'formDefinitionGuid': formDefinitionGuid,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                401: `Unauthorized`,
            },
        });
    }
    /**
     * Removes a single element from the draft (issue #1015). The live definition is untouched.
     * @param formDefinitionGuid
     * @param elementGuid
     * @returns FormDefinitionDraftResult OK
     * @throws ApiError
     */
    public deleteDraftElement(
        formDefinitionGuid: string,
        elementGuid: string,
    ): CancelablePromise<FormDefinitionDraftResult> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/api/FormDefinition/{formDefinitionGuid}/draft/elements/{elementGuid}',
            path: {
                'formDefinitionGuid': formDefinitionGuid,
                'elementGuid': elementGuid,
            },
            errors: {
                401: `Unauthorized`,
            },
        });
    }
    /**
     * Reorders the draft's elements (the inherently multi-row op) by renumbering FormOrder
     * to match the supplied guid order.
     * @param formDefinitionGuid
     * @param requestBody
     * @returns FormDefinitionDraftResult OK
     * @throws ApiError
     */
    public updateDraftElementOrder(
        formDefinitionGuid: string,
        requestBody?: Array<string>,
    ): CancelablePromise<FormDefinitionDraftResult> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/api/FormDefinition/{formDefinitionGuid}/draft/order',
            path: {
                'formDefinitionGuid': formDefinitionGuid,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                401: `Unauthorized`,
            },
        });
    }
    /**
     * Saves the draft's non-element parts (rules, section rules, form rules, meta info,
     * scalars) in one call. Used for whole-collection edits (rules) and the questionCode
     * rename ripple, where an element edit also rewrites the form's rules.
     * @param formDefinitionGuid The form whose draft is being saved.
     * @param requestBody The complete edited definition (designer save payload).
     * @returns FormDefinitionDraftResult OK
     * @throws ApiError
     */
    public updateDraft(
        formDefinitionGuid: string,
        requestBody?: FormDefinition,
    ): CancelablePromise<FormDefinitionDraftResult> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/api/FormDefinition/{formDefinitionGuid}/UpdateDraft',
            path: {
                'formDefinitionGuid': formDefinitionGuid,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                401: `Unauthorized`,
            },
        });
    }
    /**
     * Commits the form's draft: materializes the drafted definition from its header +
     * element rows, applies it to the live form in one atomic update (standard save
     * semantics — answered elements that were removed get archived), and deletes the draft.
     * @param formDefinitionGuid The form whose draft is being committed.
     * @param expectedDraftUpdatedAt Optional concurrency check: when provided and
     * the draft was saved after this timestamp, the commit is rejected with 409.
     * @returns FormDefinition OK
     * @throws ApiError
     */
    public commitDraft(
        formDefinitionGuid: string,
        expectedDraftUpdatedAt?: string,
    ): CancelablePromise<FormDefinition> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/FormDefinition/{formDefinitionGuid}/CommitDraft',
            path: {
                'formDefinitionGuid': formDefinitionGuid,
            },
            query: {
                'expectedDraftUpdatedAt': expectedDraftUpdatedAt,
            },
            errors: {
                401: `Unauthorized`,
            },
        });
    }
    /**
     * Discards the form's draft. The live definition is untouched.
     * @param formDefinitionGuid The form whose draft is being discarded.
     * @param onlyIfPristine When true, the draft is only discarded if it was never
     * saved (best-effort cleanup when a designer leaves without editing). Returns false
     * if the draft had saves and was kept.
     * @returns boolean OK
     * @throws ApiError
     */
    public discardDraft(
        formDefinitionGuid: string,
        onlyIfPristine: boolean = false,
    ): CancelablePromise<boolean> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/FormDefinition/{formDefinitionGuid}/DiscardDraft',
            path: {
                'formDefinitionGuid': formDefinitionGuid,
            },
            query: {
                'onlyIfPristine': onlyIfPristine,
            },
            errors: {
                401: `Unauthorized`,
            },
        });
    }
    /**
     * Returns the change history for a form definition (#1016), newest first — each entry
     * records who changed the form, when, a coarse summary, and the itemized per-element
     * changes. The stored form snapshot is intentionally excluded from this payload.
     * @param formDefinitionGuid The form whose history to return.
     * @returns FormDefinitionChangeDto OK
     * @throws ApiError
     */
    public getFormDefinitionHistory(
        formDefinitionGuid: string,
    ): CancelablePromise<Array<FormDefinitionChangeDto>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/FormDefinition/{formDefinitionGuid}/history',
            path: {
                'formDefinitionGuid': formDefinitionGuid,
            },
            errors: {
                401: `Unauthorized`,
            },
        });
    }
    /**
     * Updates the sort order of form definitions within a workflow.
     * This endpoint allows reordering the forms that make up a workflow.
     * The order of GUIDs in the provided list determines the new sort order of the forms.
     * Forms are assigned sequential sort order values (0, 1, 2, etc.) based on their position in the list.
     *
     * This is typically used when a user drags and drops forms to reorder them in the workflow designer UI.
     * @param workflowDefinitionGuid
     * @param requestBody An ordered list of form definition GUIDs representing the new desired order
     * @returns boolean OK
     * @throws ApiError
     */
    public updateWorkflowStepOrder(
        workflowDefinitionGuid: string,
        requestBody?: Array<string>,
    ): CancelablePromise<boolean> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/api/FormDefinition/{workflowDefinitionGuid}/UpdateWorkflowStepOrder',
            path: {
                'workflowDefinitionGuid': workflowDefinitionGuid,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                401: `Unauthorized`,
            },
        });
    }
    /**
     * Replaces an existing form definition's content with a new form structure provided as JSON.
     * This endpoint completely replaces an existing form's structure with new content.
     * It performs the following operations:
     * 1. Removes all existing components, rules, and events from the form
     * 2. Creates new sections from the uploaded form
     * 3. Creates new components and associates them with the appropriate sections
     * 4. Organizes all components in the correct order
     * 5. Updates form metadata (type, sort order)
     *
     * This is useful for importing forms from external sources or restoring forms from backups.
     * Note that all GUIDs in the new form will be regenerated to ensure uniqueness.
     * @param formDefinitionGuid The GUID of the existing form to replace
     * @param requestBody The complete new form definition structure
     * @returns FormDefinition OK
     * @throws ApiError
     */
    public uploadFormJson(
        formDefinitionGuid?: string,
        requestBody?: FormDefinition,
    ): CancelablePromise<FormDefinition> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/FormDefinition',
            query: {
                'formDefinitionGuid': formDefinitionGuid,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                401: `Unauthorized`,
            },
        });
    }
    /**
     * Creates a new form definition with the specified name.
     * This endpoint creates a new, empty form definition with the given name.
     * The form is initialized with a single default section to serve as a container for future form elements.
     * The new form has a SortOrder of 0, which can be modified later if needed.
     *
     * This is typically the first step in the form creation process, after which
     * the form designer would add specific form elements (questions, text blocks, etc.)
     * using the UpdateFormDefinition endpoint.
     * @param formName The name to assign to the new form
     * @returns FormDefinition OK
     * @throws ApiError
     */
    public createFormDefinition(
        formName: string,
    ): CancelablePromise<FormDefinition> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/FormDefinition/{formName}/CreateFormDefinition',
            path: {
                'formName': formName,
            },
            errors: {
                401: `Unauthorized`,
            },
        });
    }
    /**
     * Deletes a form definition and all its associated data.
     * This endpoint performs a complete deletion of a form definition, including:
     * - The form definition itself
     * - All form responses submitted for this form
     * - Any file uploads associated with form responses
     *
     * The operation has several security and validation checks:
     * 1. The user must be logged in
     * 2. The user must have appropriate roles in the organization that owns the form
     * 3. The form cannot be a WorkflowDefinitionMainForm (as workflows require at least one main form)
     *
     * When deleting form responses, the method also handles cleanup of any file uploads
     * to prevent orphaned files in the system.
     * @param formDefinitionGuid The unique identifier (GUID) of the form definition to delete
     * @returns FormDefinition OK
     * @throws ApiError
     */
    public deleteFormDefinition(
        formDefinitionGuid: string,
    ): CancelablePromise<FormDefinition> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/api/FormDefinition/{formDefinitionGuid}/DeleteFormDefinition',
            path: {
                'formDefinitionGuid': formDefinitionGuid,
            },
            errors: {
                401: `Unauthorized`,
            },
        });
    }
    /**
     * Duplicates a form definition and all its associated elements, rules, and events.
     * This endpoint performs a deep copy of a form definition, including:
     * - All form elements (components) with new GUIDs
     * - All form element events with new GUIDs and updated references
     * - All form element rules and their conditions with new GUIDs
     * - All form section rules with new GUIDs
     * - All form-level rules with new GUIDs
     *
     * The new form definition will:
     * - Have the same WorkflowDefinitionGuid as the original (linked to the same workflow)
     * - Have the name of the original form with " (Copy)" appended
     * - Have the same FormCode as the original (to associate related form versions together)
     * - Have FormResponseCount of 0
     * @param formDefinitionGuid The unique identifier (GUID) of the form definition to duplicate
     * @returns FormDefinition OK
     * @throws ApiError
     */
    public duplicateFormDefinition(
        formDefinitionGuid: string,
    ): CancelablePromise<FormDefinition> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/FormDefinition/{formDefinitionGuid}/DuplicateFormDefinition',
            path: {
                'formDefinitionGuid': formDefinitionGuid,
            },
            errors: {
                401: `Unauthorized`,
            },
        });
    }
    /**
     * Marks a form definition as the default version for its version group (the version a new
     * workflow instantiates), clearing the flag on its siblings.
     *
     * This deliberately bypasses the in-use draft guard that M:Compliance.Forms.Controllers.FormDefinitionController.UpdateFormDefinition(System.Guid,Compliance.Forms.Models.Forms.FormDefinition)
     * applies. Which version is the default is a pointer *about* the version group, not form
     * content: it changes nothing about what an in-use form renders, and routing it through
     * draft-and-commit would be incoherent (committing a draft replaces the live form, which is
     * not what "make this version the default" means). Only the flag is touched — no element,
     * rule or other content is read from the client.
     * @param formDefinitionGuid The version to become the default for its group.
     * @returns FormDefinition OK
     * @throws ApiError
     */
    public makeDefaultVersion(
        formDefinitionGuid: string,
    ): CancelablePromise<FormDefinition> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/FormDefinition/{formDefinitionGuid}/MakeDefaultVersion',
            path: {
                'formDefinitionGuid': formDefinitionGuid,
            },
            errors: {
                401: `Unauthorized`,
            },
        });
    }
}
